import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useCallback } from "react";
import SafeNumberInput from "./SafeNumberInput";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { ChartLauncherModal } from "./utils/ChartLauncherModal";
import { CatalyzerItem, useCatalyzerStore } from "../stores/catalyzer";
import { api } from "./utils/api";
import { ArticleViewer } from "./ArticleViewer";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { useCtrlDMark } from "./utils/useCtrlDMark";
import { HighlightedText, Mark } from "./utils/HighlightedText";
import { TickerLink } from "./TickerActionMenu";

export interface INewsItem {
  url: string;
  source: string;
  published_at: number;
  title: string;
  title_marks: Mark[];
  tickers: string;
  ticker_changes: Record<string, number>; // US ticker -> regular/premarket change_rel (whichever session applies)
  read: number; // 0, 1, 2 (0 = not read, 1 = read, 2 = bookmarked)
}

interface INewsRequest {
  query: string;
  content_query: string;
  start_date: string;
  end_date: string;
  source: string;
  page: number;
  table: string;
  saved_only: boolean;
  reverse: boolean;
  use_markers: boolean;
  us_tickers_only: boolean;
  tickers_query: string;
}

export const NewsSearch = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
}) => {
  const [news, setNews] = React.useState<INewsItem[]>([]);
  const [newsRequest, setNewsRequest] = React.useState<INewsRequest>({
    query: "",
    content_query: "",
    start_date: "",
    end_date: "",
    source: "bloomberg",
    page: 1,
    table: "articles",
    saved_only: false,
    reverse: false,
    use_markers: false,
    us_tickers_only: false,
    tickers_query: "",
  });

  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const catalyzerStore = useCatalyzerStore();

  const [catActive, setCatActive] = React.useState(false);

  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");

  const readAloud = useReadAloud([news]);

  const readableItems: ReadableItem[] = news
    .filter((item) => item.read === 0)
    .map((article) => ({ key: article.url, texts: [article.title] }));

  const onItemDone = useCallback(
    (key: string) => {
      markArticle(key, 1);
    },
    [news],
  );

  const handleCtlD = useCallback(() => {
    const firstUnread = news.find((item) => item.read === 0);
    if (firstUnread) markArticle(firstUnread.url, 1);
  }, [news]);

  const getNextScrollKey = useCallback(() => {
    const unread = news.filter((item) => item.read === 0);
    return unread[1]?.url ?? null;
  }, [news]);

  useCtrlDMark({ onTrigger: handleCtlD, getNextScrollKey, panelApi: props.api, containerApi: props.containerApi });

  function sendDataToCatalyzer(data: INewsItem[]) {
    let items: CatalyzerItem[] = [];

    for (let item of data) {
      let [dateStr, dateObj] = unixTimestampToNewYork(item.published_at);

      items.push({
        nyTS: dateObj,
        stringDate: dateStr,
        text: item.title + ` - Source: News(${item.source})`, // title is plain text
        url: item.url,
      });
    }

    catalyzerStore.setItems(items);
  }

  function toggleCat() {
    if (!catActive) {
      sendDataToCatalyzer(news);
    } else {
      catalyzerStore.setItems([]);
    }

    setCatActive(!catActive);
  }

  function handleChartOpen(
    ticker: string,
    startDate: string,
    endDate: string,
    interval: string,
  ) {
    if (!catActive) {
      setCatActive(true);
      sendDataToCatalyzer(news);
    }
    addFloatingPanel(props.containerApi, "equity_chart", {
      ticker,
      startDate,
      endDate,
      interval,
    });
  }

  // fetch bloomberg on load
  React.useEffect(() => {
    getNews();
  }, []);

  const getNews = async () => {
    setErrorMessage("");

    let startDate = newsRequest.start_date;
    let endDate = newsRequest.end_date;

    if (newsRequest.content_query.trim()) {
      if (!startDate && !endDate) {
        const dStart = new Date();
        dStart.setMonth(dStart.getMonth() - 1);
        startDate = dStart.toISOString().split("T")[0];
        const dEnd = new Date();
        dEnd.setDate(dEnd.getDate() + 1);
        endDate = dEnd.toISOString().split("T")[0];
        setNewsRequest((prev) => ({ ...prev, start_date: startDate, end_date: endDate }));
      }
    }

    setIsLoading(true);

    try {
      const req = { ...newsRequest, start_date: startDate, end_date: endDate };
      const response = await api.post<INewsItem[]>("/search", req);
      const data = response.data;

      setNews(data);

      if (catActive) sendDataToCatalyzer(data);
    } catch (error) {
      console.error("Error while fetching news:", error);
      setErrorMessage("Error while fetching news");
    } finally {
      setIsLoading(false);
    }

    // change title
    if (props.api) {
      const title = "News: " + newsRequest.query;
      props.api.setTitle(title);
    }
  };

  const markArticle = (url: string, read: number) => {
    const newMark = read % 3; // 0 = not read, 1 = read, 2 = bookmarked

    // capture the previous read value so we can revert on error
    const prevRead = news.find((item) => item.url === url)?.read ?? read;

    setNews((prevNews) =>
      prevNews.map((item) =>
        item.url === url ? { ...item, read: newMark } : item,
      ),
    );

    api
      .post("/mark", { url, read: newMark, table: "articles" })
      .then((response) => {
        const data = response.data;
        if (!data?.success) {
          console.error("data.success is false");
          // revert the change
          setNews((prevNews) =>
            prevNews.map((item) =>
              item.url === url ? { ...item, read: prevRead } : item,
            ),
          );
          throw new Error("Failed to mark news as read");
        }
      })
      .catch((error) => {
        // revert the change
        setNews((prevNews) =>
          prevNews.map((item) =>
            item.url === url ? { ...item, read: prevRead } : item,
          ),
        );
        console.error("Error marking news as read:", error);
      });
  };

  function unixTimestampToNewYork(timestamp: number): [string, Date] {
    const date = new Date(timestamp * 1000);
    // convert to New York time, format as 17 Apr 2025 HH:MM
    const newyorkDate: Date = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" }),
    );

    const day = String(newyorkDate.getDate()).padStart(2, "0");
    const month = String(newyorkDate.getMonth() + 1).padStart(2, "0");
    const year = newyorkDate.getFullYear();
    const hours = String(newyorkDate.getHours()).padStart(2, "0");
    const minutes = String(newyorkDate.getMinutes()).padStart(2, "0");
    const seconds = String(newyorkDate.getSeconds()).padStart(2, "0");

    // get the day of the week
    const options: Intl.DateTimeFormatOptions = { weekday: "short" };
    const dayOfWeek = newyorkDate.toLocaleDateString("en-US", options);

    return [
      `${year}-${month}-${day} ${hours}:${minutes} (${dayOfWeek})`,
      newyorkDate,
    ];
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* Scrollable: search controls + articles list */}
      <div className="flex-1 overflow-auto p-2">
      <div className="flex gap-4">
        <button
          className={` mt-5 btn w-12 h-12 ${catActive ? "btn-primary" : "btn-default"}`}
          onClick={(e) => {
            e.preventDefault();
            toggleCat();
          }}
        >
          CAT
        </button>

        <form
          className="flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            getNews();
          }}
        >
          <div className="flex gap-4">
            <div className="form-group w-[150px]">
              <label htmlFor="page">Page:</label>
              <SafeNumberInput
                id="page"
                value={newsRequest.page}
                onChange={(value) =>
                  setNewsRequest({ ...newsRequest, page: value })
                }
                min={1}
                max={10000}
              />
            </div>

            <div className="form-group">
              <label htmlFor="start_date">Start Date:</label>
              <input
                type="text"
                id="start_date"
                placeholder="YYYY-MM-DD"
                value={newsRequest.start_date}
                onChange={(e) =>
                  setNewsRequest({ ...newsRequest, start_date: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date:</label>
              <input
                type="text"
                id="end_date"
                placeholder="YYYY-MM-DD"
                value={newsRequest.end_date}
                onChange={(e) =>
                  setNewsRequest({ ...newsRequest, end_date: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="source">Source:</label>
              <select
                id="source"
                value={newsRequest.source}
                onChange={(e) => {
                  setNewsRequest({ ...newsRequest, source: e.target.value });
                }}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="">All</option>

                <optgroup label="Main">
                  <option value="bloomberg">Bloomberg</option>
                  <option value="reuters">Reuters</option>
                  <option value="wsj">WSJ</option>
                  <option value="barrons">Barron's</option>
                  <option value="inv_news">Inv News</option>
                  <option value="cnbc">CNBC</option>
                </optgroup>

                <optgroup label="Political">
                  <option value="politico">Politico</option>
                  <option value="the_hill">The Hill</option>
                  <option value="axios">Axios</option>
                </optgroup>

                <optgroup label="Papers">
                  <option value="ft">Financial Times</option>
                  <option value="la_times">Los Angeles Times</option>
                  <option value="ny_times">NY Times</option>
                  <option value="ny_post">NY Post</option>
                  <option value="washington_post">Washington Post</option>
                </optgroup>

                <optgroup label="Wires">
                  <option value="ap">AP</option>
                  <option value="marketwatch">MarketWatch</option>
                </optgroup>

                <optgroup label="TV">
                  <option value="fox">Fox News</option>
                  <option value="nbc">NBC</option>
                  <option value="cbs">CBS</option>
                  <option value="cnn">CNN</option>
                  <option value="abc">ABC</option>
                </optgroup>

                <optgroup label="Crypto">
                  <option value="coindesk">CoinDesk</option>
                  <option value="cointelegraph">CoinTelegraph</option>
                  <option value="decrypt">Decrypt</option>
                </optgroup>
              </select>
            </div>

            <div className="form-group w-[300px]">
              <label htmlFor="keywords">Title Q:</label>
              <input
                type="text"
                id="keywords"
                value={newsRequest.query}
                onChange={(e) =>
                  setNewsRequest({ ...newsRequest, query: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="form-group">
              <label htmlFor="us_tickers_only">
                <input
                  type="checkbox"
                  id="us_tickers_only"
                  checked={newsRequest.us_tickers_only}
                  onChange={(e) =>
                    setNewsRequest({
                      ...newsRequest,
                      us_tickers_only: e.target.checked,
                    })
                  }
                />
                &nbsp; US Tickers Only
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="saved_only">
                <input
                  type="checkbox"
                  id="saved_only"
                  checked={newsRequest.saved_only}
                  onChange={(e) =>
                    setNewsRequest({
                      ...newsRequest,
                      saved_only: e.target.checked,
                    })
                  }
                />
                &nbsp; Saved only
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="reverse">
                <input
                  type="checkbox"
                  id="reverse"
                  checked={newsRequest.reverse}
                  onChange={(e) =>
                    setNewsRequest({
                      ...newsRequest,
                      reverse: e.target.checked,
                    })
                  }
                />
                &nbsp; Reverse
              </label>
            </div>


            <div className="form-group w-[200px]">
              <label htmlFor="tickers_query">Tickers Query:</label>
              <input
                type="text"
                id="tickers_query"
                value={newsRequest.tickers_query}
                onChange={(e) =>
                  setNewsRequest({
                    ...newsRequest,
                    tickers_query: e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group w-[200px]">
              <label htmlFor="content_query">Content Q:</label>
              <input
                type="text"
                id="content_query"
                value={newsRequest.content_query}
                onChange={(e) =>
                  setNewsRequest({ ...newsRequest, content_query: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>&nbsp;</label>
              <button
                type="button"
                
                onClick={() => setNews((prev) => prev.map((item) => ({ ...item, read: 0 })))}
              >
                Unread all
              </button>
            </div>

            <button type="submit" className="hidden">
              Search
            </button>
          </div>
        </form>
      </div>

      <ArticlesTable
        api={props.api}
        containerApi={props.containerApi}
        news={news}
        errorMessage={errorMessage}
        isLoading={isLoading}
        markArticle={markArticle}
        unixTimestampToNewYork={unixTimestampToNewYork}
        setActiveArticleUrl={setActiveArticleUrl}
        currentlyReadingKey={readAloud.currentlyReadingKey}
        catActive={catActive}
        onChartOpen={handleChartOpen}
      />
      </div>{/* end scrollable */}

      {activeArticleUrl !== "" && (
        <ArticleViewer
          url={activeArticleUrl}
          setActiveArticleUrl={setActiveArticleUrl}
          contentQuery={newsRequest.content_query}
        />
      )}

      {/* ReadAloud bar pinned to bottom */}
      {activeArticleUrl === "" && (
        <ReadAloudBar
          voiceControls={readAloud.voiceControls}
          setVoiceControls={readAloud.setVoiceControls}
          toggleReading={readAloud.toggleReading}
          skipItem={readAloud.skipItem}
          items={readableItems}
          onItemDone={onItemDone}
          idPrefix="news-"
          containerApi={props.containerApi}
          panelApi={props.api}
        />
      )}
    </div>
  );
};

const ArticlesTable = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
  news: INewsItem[];
  errorMessage: string;
  isLoading: boolean;
  markArticle: (url: string, mark: number) => void;
  unixTimestampToNewYork: (timestamp: number) => [string, Date];
  setActiveArticleUrl: (url: string) => void;
  currentlyReadingKey: string;
  catActive: boolean;
  onChartOpen: (
    ticker: string,
    startDate: string,
    endDate: string,
    interval: string,
  ) => void;
}) => {
  const setHoveredItemIndex = useCatalyzerStore((s) => s.setHoveredItemIndex);
  const hoveredItemIndex = useCatalyzerStore((s) => s.hoveredItemIndex);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [chartModalPublishedAt, setChartModalPublishedAt] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    if (hoveredItemIndex !== null && itemRefs.current[hoveredItemIndex]) {
      itemRefs.current[hoveredItemIndex]!.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [hoveredItemIndex]);

  if (props.isLoading) {
    return <div className="text-center">Loading...</div>;
  }

  if (props.errorMessage !== "") {
    return <p className="text-center">{props.errorMessage}</p>;
  }

  if (props.news.length === 0) {
    return <p className="text-center">No news found</p>;
  }

  function isUsTicker(ticker: string): boolean {
    return ticker.length <= 5 && !ticker.includes(":") && !ticker.includes(".");
  }

  function addMarkClass(mark: number): string {
    switch (mark) {
      case 0:
        return "bg-gray-400";
      case 1:
        return "bg-blue-600";
      case 2:
        return "bg-yellow-600";
      default:
        return "bg-gray-400";
    }
  }

  return (
    <div className="mb-20">
      {props.news.map((article, index) => (
        <div
          key={article.url}
          ref={(el) => {
            itemRefs.current[index] = el;
          }}
          className={`flex gap-4 mb-4 ${props.currentlyReadingKey === article.url ? "bg-yellow-100" : ""} ${hoveredItemIndex === index ? "bg-yellow-100" : ""}`}
          id={article.url}
          onMouseEnter={() => {
            if (props.catActive) setHoveredItemIndex(index);
          }}
          onMouseLeave={() => {
            if (props.catActive) setHoveredItemIndex(null);
          }}
        >
          <div
            className={`min-w-5 cursor-pointer ${addMarkClass(article.read)}`}
            onClick={() => {
              props.markArticle(article.url, article.read + 1);
            }}
          ></div>

          <div>
            <p className="text-gray-500 gap-4 flex">
              <span>
                {props.unixTimestampToNewYork(article.published_at)[0]}
              </span>
              <a href={article.url} target="_blank" rel="noopener noreferrer">
                ({article.source})
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  props.setActiveArticleUrl(article.url);
                }}
              >
                (text)
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  setChartModalPublishedAt(article.published_at);
                }}
              >
                (chart)
              </a>
            </p>
            <div className="whitespace-pre-wrap prose prose-lg max-w-prose leading-relaxed font-sans">
              {article.tickers && (
                <mark className="ner-loc mr-1">
                  {article.tickers.split(" ").map((t, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && " "}
                      {isUsTicker(t) ? (
                        <TickerLink ticker={t} containerApi={props.containerApi} className="font-semibold cursor-pointer hover:underline" />
                      ) : (
                        t
                      )}
                      {article.ticker_changes?.[t] !== undefined && (
                        <span className="underline"> {article.ticker_changes[t].toFixed(1)}%</span>
                      )}
                    </React.Fragment>
                  ))}
                </mark>
              )}
              <HighlightedText text={article.title} marks={article.title_marks} />
            </div>
          </div>

          <hr />
        </div>
      ))}

      {chartModalPublishedAt !== null && (
        <ChartLauncherModal
          publishedAt={chartModalPublishedAt}
          onSubmit={props.onChartOpen}
          onClose={() => setChartModalPublishedAt(null)}
        />
      )}
    </div>
  );
};
